import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/socketContext';
import './ProfilePage.css';
import { useAuth } from '../contexts/authContext';
import { API_URL } from '../lib/config';
import NewProfileCard from '../components/NewProfileCard';
import ProfileCreationLoader from '../components/ProfileCreationLoader';
import AddFriendManager from '../components/AddFriendManager';

function ProfilePage() {
    const { currentUser } = useAuth();
    
    const [displayItems, setDisplayItems] = useState([]);
    // [수정] currentIndex의 초기값을 sessionStorage에서 가져오도록 설정
    const [currentIndex, setCurrentIndex] = useState(() => {
        const savedIndex = sessionStorage.getItem('profileCarouselIndex');
        return savedIndex ? parseInt(savedIndex, 10) : 0;
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const socket = useSocket();

    const [userGlobalCooldown, setUserGlobalCooldown] = useState({ isOnCooldown: false, cooldownEndTime: null });
    const [isPlus, setIsPlus] = useState(false); // ⬅️ [추가] PLUS 유저인지 저장할 state
    const [isCreatingProfile, setIsCreatingProfile] = useState(false);
    
    const [showCreateProfilePrompt, setShowCreateProfilePrompt] = useState(false);
    const [selectedChatRoomForProfile, setSelectedChatRoomForProfile] = useState(null);

    const [showProfileCreationStatus, setShowProfileCreationStatus] = useState(false);
    const [newlyCreatedProfileData, setNewlyCreatedProfileData] = useState(null);

    const loggedInUserIdRef = useRef(null);
    const idTokenRef = useRef(null);

    const formatChatTimestamp = (isoString) => {
        if (!isoString) return '정보 없음';
        try {
            const date = new Date(isoString);
            return `${date.getFullYear()}년 ${(date.getMonth() + 1).toString().padStart(2, '0')}월 ${date.getDate().toString().padStart(2, '0')}일 ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
        } catch (e) { return '날짜 오류'; }
    };

    const formatCooldownEndTime = useCallback((isoString) => {
        if (!isoString) return '정보 없음';
        try { return new Date(isoString).toLocaleString('ko-KR'); } 
        catch (e) { return '시간 정보 오류'; }
    }, []);

    const fetchData = useCallback(async (url, token) => {
        const response = await fetch(url, {
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
    }, []);

    const loadAllItems = useCallback(async (token) => {
        setLoading(true);
        setError(null);
        try {
            const [userData, chatRoomData] = await Promise.all([
                fetchData(`${API_URL}/api/login/me`, token),
                fetchData(`${API_URL}/api/chat/profile-eligible-chatrooms`, token)
            ]);
            // ▼▼▼ [추가] PLUS 유저인지 판별 ▼▼▼
            const userSubStatus = userData.user?.subscriptionStatus;
            const userCurrentPeriodEnd = userData.user?.currentPeriodEnd;
            const isPlusUser = userSubStatus === 'active' && new Date(userCurrentPeriodEnd) > new Date();
            setIsPlus(isPlusUser);
            // ▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲▲
            const fetchedProfiles = (userData.user?.profiles || []).map(p => ({ ...p, type: 'profile', id: p._id }));
            loggedInUserIdRef.current = userData.user?.id;
            // const eligibleRooms = (chatRoomData.eligibleChatRooms || []).map(room => ({ ...room, type: 'chatroom', id: room.chatRoomId }));
            // ▼▼▼ [수정할 코드] type이 'chatroom'으로 덮어씌워지기 전에 원래 방 타입(AI_FRIEND 등)을 보존해 둡니다. ▼▼▼
            const eligibleRooms = (chatRoomData.eligibleChatRooms || []).map(room => ({ 
                ...room, 
                originalRoomType: room.type || room.roomType, // 원래 타입 백업
                type: 'chatroom', 
                id: room.chatRoomId 
            }));
            setUserGlobalCooldown(chatRoomData.userProfileCreationCooldown || { isOnCooldown: false, cooldownEndTime: null });
            const allItems = [...fetchedProfiles, ...eligibleRooms];
            setDisplayItems(allItems);

            // [수정] 인덱스 설정 로직을 명확한 우선순위로 변경
            const savedIndex = sessionStorage.getItem('profileCarouselIndex');
            const representativeIndex = allItems.findIndex(item => item.type === 'profile' && item.isRepresentative);

            // 우선순위: 1. 저장된 인덱스 -> 2. 대표 프로필 -> 3. 첫 번째(0)
            if (savedIndex && parseInt(savedIndex, 10) < allItems.length) {
                setCurrentIndex(parseInt(savedIndex, 10));
            } else if (representativeIndex !== -1) {
                setCurrentIndex(representativeIndex);
            } else {
                setCurrentIndex(0);
            }
        } catch (error) {
            setError("데이터를 불러오는 데 실패했습니다.");
        } finally {
            setLoading(false);
        }
    }, [fetchData]);

    useEffect(() => {
        if (currentUser) {
            currentUser.getIdToken().then(token => {
                idTokenRef.current = token;
                loadAllItems(token);
            }).catch(err => setError("인증 정보를 가져오는 데 실패했습니다."));
        }

        // [추가] 컴포넌트가 언마운트될 때(다른 페이지로 이동 시) sessionStorage를 정리하지 않도록 합니다.
        // 대신, 사용자가 탭을 닫으면 sessionStorage는 자동으로 사라집니다.
        return () => {
            // cleanup logic if needed
        };
    }, [currentUser, loadAllItems]);

    // [수정] currentIndex가 변경될 때마다 sessionStorage에 저장
    useEffect(() => {
        // displayItems가 로드된 후에만 인덱스를 저장하여 초기화 문제를 방지
        if (displayItems.length > 0) {
            sessionStorage.setItem('profileCarouselIndex', currentIndex);
        }
    }, [currentIndex, displayItems]);
    
    useEffect(() => {
        if (!socket) return;
        const handleProfileCreationFeedback = ({ success, message, profile }) => {
            setIsCreatingProfile(false);
            setShowCreateProfilePrompt(false);
            if (success) {
                setNewlyCreatedProfileData(profile);
                setShowProfileCreationStatus(true);
            } else {
                alert(`프로필 생성 실패: ${message}`);
            }
            if (idTokenRef.current) loadAllItems(idTokenRef.current);
        };
        socket.on('profile_creation_feedback', handleProfileCreationFeedback);
        return () => socket.off('profile_creation_feedback', handleProfileCreationFeedback);
    }, [socket, loadAllItems]);

    const handleSetRepresentative = async (profileId) => {
        const token = idTokenRef.current;
        if (!token) return alert('인증 정보가 유효하지 않습니다.');
        try {
            const response = await fetch(`${API_URL}/api/profiles/profile/set-representative/${profileId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('대표 프로필 설정 실패');
            alert('대표 프로필이 성공적으로 설정되었습니다!');
            setShowProfileCreationStatus(false);
            loadAllItems(token);
        } catch (e) {
            alert('대표 프로필 설정 중 오류가 발생했습니다.');
        }
    };

    const handleDeleteChatRoom = async (chatRoomId) => {
        if (window.confirm("이 채팅 기록을 정말로 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) {
            const token = idTokenRef.current;
            if (!token) return alert('인증 정보가 유효하지 않습니다.');
            try {
                const response = await fetch(`${API_URL}/api/chat/chat-room/${chatRoomId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (!response.ok) {
                    if (response.status === 404) {
                        if (process.env.NODE_ENV === 'development') {
                            console.log("채팅방이 이미 삭제되었습니다. 목록을 새로고침합니다.");
                        }
                        loadAllItems(token);
                        return;
                    }
                    const errorData = await response.json();
                    throw new Error(errorData.message || '삭제에 실패했습니다.');
                }

                const data = await response.json();
                alert(data.message);
                loadAllItems(token);
            } catch (e) {
                alert(`채팅 기록 삭제 중 오류가 발생했습니다: ${e.message}`);
            }
        }
    };

    const handleConfirmNewlyCreatedProfile = () => {
        setShowProfileCreationStatus(false);
        setNewlyCreatedProfileData(null);
    };

    const handlePrev = () => setCurrentIndex(prev => (prev === 0 ? displayItems.length - 1 : prev - 1));
    const handleNext = () => setCurrentIndex(prev => (prev === displayItems.length - 1 ? 0 : prev + 1));

    const handleCreateProfileClick = (chatRoom) => {
        // ▼▼▼ [수정] PLUS 유저가 아닐 때(!isPlus)만 쿨타임 경고를 띄우고 막습니다! ▼▼▼
        if (!isPlus && userGlobalCooldown.isOnCooldown) {
            alert(`AI 프로필 생성은 쿨타임 중입니다. ${formatCooldownEndTime(userGlobalCooldown.cooldownEndTime)} 이후에 다시 시도해주세요.`);
            return;
        }
        setSelectedChatRoomForProfile(chatRoom);
        setShowCreateProfilePrompt(true);
    };

    const confirmCreateProfile = () => {
        const userId = loggedInUserIdRef.current;
        if (!selectedChatRoomForProfile || !socket || !userId) {
            alert('프로필 생성에 필요한 정보가 부족합니다.');
            return;
        }
        setShowCreateProfilePrompt(false);
        setIsCreatingProfile(true);
        socket.emit('request_ai_profile_analysis', { userId, chatRoomId: selectedChatRoomForProfile.id });
    };

    // [추가] AI 프로필 삭제 처리 함수
    const handleDeleteProfile = async (profileId, profileNickname) => {
        if (window.confirm(`'${profileNickname}' 프로필을 정말로 삭제하시겠습니까?`)) {
            const token = idTokenRef.current;
            if (!token) return alert('인증 정보가 유효하지 않습니다.');
            try {
                const response = await fetch(`${API_URL}/api/profiles/profile/${profileId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.message || '프로필 삭제에 실패했습니다.');
                }

                alert(data.message);
                loadAllItems(token); // 목록 새로고침
            } catch (e) {
                alert(`프로필 삭제 중 오류가 발생했습니다: ${e.message}`);
            }
        }
    };

    // [추가] 읽기 전용 채팅방으로 이동하는 함수
    const handleViewChatLog = (chatRoomItem) => {
        navigate(`/chat`, { 
            state: { 
                chatRoomId: chatRoomItem.id, 
                partner: { // partner 정보를 재구성
                    nickname: chatRoomItem.partnerNickname,
                    character: chatRoomItem.partnerCharacter
                    // character 정보는 없으므로 비워둠
                },
                isReadOnly: true, // ★★★ 읽기 전용 모드임을 알리는 플래그 ★★★
                from: '/profile'
            } 
        });
    };

    // 로딩 화면은 배경 컨셉을 유지하도록 수정
    if (loading) {
        return (
            <div className="profile-page-container">
                <div id="stars"></div>
                <div id="stars2"></div>
                <div id="stars3"></div>
                <div className="loading-message">로딩 중...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="profile-page-container">
                <div id="stars"></div>
                <div id="stars2"></div>
                <div id="stars3"></div>
                <div className="error">{error}</div>
                <button className="back-to-home" onClick={() => navigate('/')}>홈으로 돌아가기</button>
            </div>
        );
    }
    
    if (isCreatingProfile) {
        return (
            <div className="profile-page-container">
                <div id="stars"></div>
                <div id="stars2"></div>
                <div id="stars3"></div>
                <ProfileCreationLoader />
            </div>
        );
    }

    if (showProfileCreationStatus && newlyCreatedProfileData) {
        return (
            <div className="profile-page-container">
                <div id="stars"></div>
                <div id="stars2"></div>
                <div id="stars3"></div>
                <NewProfileCard profile={newlyCreatedProfileData}>
                    <button
                        className='npc-button npc-button-primary' // 스타일 통일을 위해 클래스명 변경
                        onClick={()=> handleSetRepresentative(newlyCreatedProfileData.id)}
                    >
                        대표 프로필로 설정하기    
                    </button>    
                    <button
                        className='npc-button npc-button-secondary' // 스타일 통일을 위해 클래스명 변경
                        onClick={handleConfirmNewlyCreatedProfile}
                    >
                        확인
                    </button>
                </NewProfileCard>
            </div>
        );
    }

    const currentItem = displayItems.length > 0 ? displayItems[currentIndex] : null;

    return (
        <div className="profile-page-container">
            {/* ▼▼▼ [수정] 별 배경 div 추가 ▼▼▼ */}
            <div id="stars"></div>
            <div id="stars2"></div>
            <div id="stars3"></div>

            <h1>내 프로필/지난대화</h1>

            {currentItem ? (
                <>
                    <div className="carousel-container">
                        <button className="carousel-nav-button prev" onClick={handlePrev}>&#10094;</button>
                        <div key={currentItem.id} className="profile-card">
                            {currentItem.type === 'profile' ? (
                                <>
                                    {currentIndex > 0 && (
                                        <button 
                                        className="card-delete-button" 
                                        onClick={() => handleDeleteProfile(currentItem.id, currentItem.nickname)}
                                        onTouchEnd={() => handleDeleteProfile(currentItem.id, currentItem.nickname)}
                                    >
                                        &times;
                                    </button>
                                    )}
                                    <h2>{currentItem.nickname}</h2>
                                    <p>{currentItem.character}</p>
                                    <p className="status-text">{currentItem.isRepresentative ? '대표 프로필' : ''}</p>
                                    {!currentItem.isRepresentative && (
                                        <button className="npc-button npc-button-primary" onClick={() => handleSetRepresentative(currentItem.id)}>
                                            대표 프로필로 설정
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    <button className="card-delete-button"
                                        onClick={() => handleDeleteChatRoom(currentItem.id)}
                                        onTouchEnd={() => handleDeleteChatRoom(currentItem.id)}
                                    >
                                        &times;
                                    </button>
                                    <h2>AI 프로필 생성 가능</h2>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        gap: '8px',
                                        marginBottom: '10px' // 기존 h3의 margin 고려
                                    }}>
                                        <h3 style={{ margin: 0 }}>상대방: {currentItem.partnerNickname}</h3>
                                        
                                        {/* ▼▼▼ [수정] partnerId가 있고, 방 타입이 'AI_FRIEND'가 아닐 때만 친추 버튼 표시 ▼▼▼ */}
                                        {currentItem.partnerId && currentItem.originalRoomType !== 'AI_FRIEND' && (
                                            <AddFriendManager 
                                                partnerId={currentItem.partnerId} 
                                                defaultNickname={currentItem.partnerNickname}
                                            />
                                        )}
                                    </div>
                                    <p>마지막 채팅: {formatChatTimestamp(currentItem.lastChatTimestamp)}</p>
                                    <button className="last-message-button" onClick={() => handleViewChatLog(currentItem)}>
                                        <p className="last-message-label">마지막 메시지:</p>
                                        <p className="last-message-content">"{currentItem.lastMessageText}"</p>
                                        <span className="view-log-prompt">(클릭하여 대화 기록 보기)</span>
                                    </button>
                                    <div className="card-actions">
                                        {/* ▼▼▼ [수정] PLUS 유저가 아니고, 쿨타임 중일 때만 막습니다. ▼▼▼ */}
                                        {!isPlus && userGlobalCooldown.isOnCooldown ? (
                                            <button className="cooldown-display-button" disabled>
                                                쿨타임 종료: {formatCooldownEndTime(userGlobalCooldown.cooldownEndTime)}
                                            </button>
                                        ) : (
                                            <button 
                                                className="create-profile-button" 
                                                onClick={() => handleCreateProfileClick(currentItem)} 
                                                disabled={isCreatingProfile}
                                                // PLUS 혜택으로 뚫었다면 시각적 피드백을 주는 것도 좋습니다.
                                                style={isPlus && userGlobalCooldown.isOnCooldown ? { border: '2px solid #FFD700', boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)' } : {}}
                                            >
                                                {isPlus && userGlobalCooldown.isOnCooldown ? '✨ 프로필 생성하기 (PLUS 무제한)' : '프로필 생성하기'}
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                        <button className="carousel-nav-button next" onClick={handleNext}>&#10095;</button>
                        <p className="profile-counter">{currentIndex + 1} / {displayItems.length}</p>
                    </div>
                </>
            ) : (
                <div className="no-items">
                    <p>생성된 프로필이 없습니다.</p>
                    <p>채팅을 통해 AI 프로필을 만들어보세요!</p>
                </div>
            )}
            
            {showCreateProfilePrompt && selectedChatRoomForProfile && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>AI 프로필 생성</h3>
                        <p>선택하신 채팅방({selectedChatRoomForProfile.partnerNickname}님과의 대화)을 기반으로 AI 프로필을 생성하시겠습니까?</p>
                        {isCreatingProfile ? (
                            <div className="loading-spinner"></div>
                        ) : (
                            <div className="modal-buttons">
                                <button className="confirm-button" onClick={confirmCreateProfile}>생성하기</button>
                                <button className="cancel-button" onClick={() => setShowCreateProfilePrompt(false)}>취소</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <button className="back-to-home" onClick={() => navigate('/')}>홈으로 돌아가기</button>
        </div>
    );
}

export default ProfilePage;